import { queryOptions, keepPreviousData } from '@tanstack/react-query';
import { legacyOrderApi, type LegacyOrdersQuery } from './legacy-order-api';

/** Query-key factory for the legacy-orders slice. */
export const legacyOrderKeys = {
  all: ['legacy-orders'] as const,
  lists: () => [...legacyOrderKeys.all, 'list'] as const,
  list: (query: LegacyOrdersQuery) => [...legacyOrderKeys.lists(), query] as const,
  details: () => [...legacyOrderKeys.all, 'detail'] as const,
  detail: (id: number) => [...legacyOrderKeys.details(), id] as const,
};

export const legacyOrderQueries = {
  list: (query: LegacyOrdersQuery) =>
    queryOptions({
      queryKey: legacyOrderKeys.list(query),
      queryFn: () => legacyOrderApi.list(query),
      // The old grid never blanked between pages or filter changes — it kept
      // the previous rows visible while the next set loaded. Without this the
      // table would flash an empty state on every pill click.
      placeholderData: keepPreviousData,
    }),

  /**
   * One order, for the detail drawer. `enabled` is the caller's job — the
   * drawer passes the open order's id, or null when closed.
   *
   * The 15s interval reproduces the old drawer's stage poll (`OrderSingleBox`
   * re-read the order every 15s and refetched on a stage change) — it is the
   * drawer's only live-update path while the websocket rejects merchant
   * tokens.
   */
  detail: (id: number) =>
    queryOptions({
      queryKey: legacyOrderKeys.detail(id),
      queryFn: () => legacyOrderApi.detail(id),
      refetchInterval: 15_000,
    }),

  /**
   * The courier's live position, polled every 5s while the order is in
   * flight — the old drawer's `courierInfo` query verbatim (enabled/refetch
   * gating included at the hook layer).
   */
  courierTracking: (input: { orderId: number; courierId: number }) =>
    queryOptions({
      queryKey: [
        ...legacyOrderKeys.detail(input.orderId),
        'tracking',
        input.courierId,
      ] as const,
      queryFn: () => legacyOrderApi.courierTracking(input),
      refetchInterval: 5_000,
    }),
};
